/**
 * MultiSelect Field Component
 * FormBuilder compatible multiselect component
 */
import React, { useMemo, useCallback, ReactNode } from 'react'
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Box,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Typography,
  useTheme,
  IconButton
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Controller } from 'react-hook-form'
import { FormFieldProps, FieldOption } from '../types'
import { useFormContext } from '../FormContext'

// Convert FieldOption to MultiSelect Option format
interface MultiSelectOption {
  id: FieldOption['value']
  name: string
}

type MultiSelectValue = FieldOption['value']

const MultiSelect: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    options = [],
    required,
    disabled,
    helperText,
    dataSource,
    fieldProps = {}
  } = config

  const theme = useTheme()

  // Get the form context for data source and change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // Get the data source data
  const dataSources = formContext?.dataSources || {}
  const dataSourceState = dataSource ? dataSources[dataSource] : null

  // Merge options: use data source options first, then config options
  const mergedOptions = useMemo<FieldOption[]>(() => {
    if (
      dataSource &&
      dataSourceState?.options &&
      dataSourceState.options.length > 0
    ) {
      return dataSourceState.options
    }
    return options
  }, [dataSource, dataSourceState, options])

  // Convert FieldOption to MultiSelect Option format
  const multiSelectOptions = useMemo<MultiSelectOption[]>(() => {
    return mergedOptions.map(option => ({
      id: option.value,
      name: option.label
    }))
  }, [mergedOptions])

  // Add "All" option if allCheckedValue is configured
  const { allCheckedValue, sx: fieldSx, ...otherFieldProps } = fieldProps
  const allOptions = useMemo(() => {
    if (allCheckedValue) {
      return [{ id: allCheckedValue, name: 'All' }, ...multiSelectOptions]
    }
    return multiSelectOptions
  }, [multiSelectOptions, allCheckedValue])

  // Check if the field is changed for highlight
  const isFieldChanged = () => {
    if (config.enableChangeHighlight !== true) {
      return false
    }

    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // Compare arrays
      const origArray = Array.isArray(originalValue) ? originalValue : []
      const currArray = Array.isArray(currentValue) ? currentValue : []

      // Check if arrays are different
      if (origArray.length !== currArray.length) {
        return true
      }

      return !origArray.every((val, index) => val === currArray[index])
    }
    return false
  }

  const isChanged = isFieldChanged()

  // Check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // Check if the data source is loading
  const isLoading = dataSource && dataSourceState?.loading === true

  // Handle multiselect logic
  const handleMultiSelectChange = useCallback(
    (
      newValues: MultiSelectValue[],
      currentValues: MultiSelectValue[],
      onChange: (value: MultiSelectValue[]) => void
    ) => {
      if (!allCheckedValue) {
        onChange(newValues)
        return
      }

      const nonAllOptions = multiSelectOptions.map(o => o.id)
      const hadAll = currentValues.includes(allCheckedValue)

      // Determine what user clicked by comparing the differences
      const addedValues = newValues.filter(v => !currentValues.includes(v))
      const removedValues = currentValues.filter(v => !newValues.includes(v))

      // Case 1: User clicked "All"
      if (
        addedValues.includes(allCheckedValue) ||
        removedValues.includes(allCheckedValue)
      ) {
        if (hadAll) {
          // "All" was selected, user clicked it to deselect all
          onChange([])
        } else {
          // "All" was not selected, user clicked it to select all
          onChange([allCheckedValue, ...nonAllOptions])
        }
      } else {
        // Case 2: User clicked individual option(s)
        const otherValues = newValues.filter(v => v !== allCheckedValue)

        // Check if all individual options are now selected
        if (
          otherValues.length === nonAllOptions.length &&
          nonAllOptions.length > 0
        ) {
          // All individual options selected, add "All"
          onChange([allCheckedValue, ...nonAllOptions])
        } else {
          // Not all individual options selected, remove "All" if it was there
          onChange(otherValues)
        }
      }
    },
    [allCheckedValue, multiSelectOptions]
  )

  const handleDeleteChip = useCallback(
    (
      e: React.MouseEvent,
      chipValue: MultiSelectValue,
      currentValues: MultiSelectValue[],
      onChange: (value: MultiSelectValue[]) => void
    ) => {
      e.stopPropagation()
      const newValues = currentValues.filter(v => v !== chipValue)
      if (allCheckedValue && chipValue === allCheckedValue) {
        // If removing "All", remove all values
        onChange([])
      } else {
        // If removing individual item, also remove "All" if it exists
        onChange(newValues.filter(v => v !== allCheckedValue))
      }
    },
    [allCheckedValue]
  )

  const chipStyles = useCallback(
    (chipValue: MultiSelectValue) => {
      const isAllChecked = chipValue === allCheckedValue
      return {
        color: !isAllChecked ? 'white' : theme.palette.text.primary,
        backgroundColor: !isAllChecked
          ? theme.palette.secondary.dark
          : undefined,
        marginRight: 1,
        maxHeight: '24px',
        '& .MuiChip-deleteIcon': {
          width: '16px',
          height: '16px',
          color: !isAllChecked ? 'white' : undefined
        }
      }
    },
    [allCheckedValue, theme.palette]
  )

  const renderValue = useCallback(
    (
      selected: MultiSelectValue[],
      onChange: (value: MultiSelectValue[]) => void
    ) => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          position: 'relative',
          '&:hover .clear-all-button': {
            opacity: 1
          }
        }}>
        {selected.length > 0 && (
          <Chip
            key={selected[0]}
            label={
              <Typography fontSize={13} sx={{ lineHeight: '100%' }}>
                {allOptions.find(option => option.id === selected[0])?.name}
              </Typography>
            }
            onDelete={e => handleDeleteChip(e, selected[0], selected, onChange)}
            onMouseDown={e => e.stopPropagation()}
            sx={chipStyles(selected[0])}
          />
        )}
        {selected.length > 1 &&
          !selected.includes(allCheckedValue as MultiSelectValue) && (
            <Typography
              sx={{
                color: theme => theme.palette.secondary.dark
              }}
              fontSize={16}
              fontWeight={'bold'}>
              +{selected.length - 1}
            </Typography>
          )}
        {selected.length > 0 && (
          <IconButton
            size='small'
            className='clear-all-button'
            onClick={e => {
              e.stopPropagation()
              onChange([])
            }}
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            sx={{
              position: 'absolute',
              right: 0,
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}>
            <ClearIcon fontSize='small' />
          </IconButton>
        )}
      </Box>
    ),
    [allOptions, handleDeleteChip, chipStyles, allCheckedValue]
  )

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field }) => {
        // Ensure field.value is always an array
        const fieldValue = Array.isArray(field.value)
          ? (field.value as MultiSelectValue[])
          : []

        const onChangeSelect = (
          event: SelectChangeEvent<MultiSelectValue[]>
        ) => {
          handleMultiSelectChange(
            event.target.value as MultiSelectValue[],
            fieldValue,
            field.onChange
          )
        }

        // Handle error state
        const hasError =
          Boolean(errors[name]) || Boolean(dataSource && dataSourceState?.error)
        const errorMessage =
          errors[name]?.message ||
          (dataSource && dataSourceState?.error
            ? 'Failed to load options'
            : helperText)

        return (
          <FormControl
            fullWidth
            error={hasError}
            disabled={disabled || isReadOnly || Boolean(isLoading)}>
            <InputLabel id={`${name}-label`} shrink>
              {label}
            </InputLabel>
            <Select
              labelId={`${name}-label`}
              id={name}
              multiple
              value={fieldValue}
              onChange={
                onChangeSelect as (
                  event: SelectChangeEvent<unknown>,
                  child: ReactNode
                ) => void
              }
              input={<OutlinedInput notched label={label} />}
              renderValue={selected =>
                renderValue(selected as MultiSelectValue[], field.onChange)
              }
              sx={{
                minWidth: '100%',
                height: '36px',
                '& .MuiSelect-select': {
                  ...(isChanged && {
                    color: 'secondary.dark',
                    fontWeight: 'bold'
                  })
                },
                ...(fieldSx || {})
              }}
              name={name}
              aria-label={label}
              {...otherFieldProps}>
              {allOptions.map(option => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  sx={{
                    padding: 0,
                    height: '36px'
                  }}>
                  <Checkbox
                    checked={fieldValue.includes(option.id)}
                    aria-label={`Select ${option.name}`}
                  />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </Select>
            {errorMessage && (
              <Typography
                color={hasError ? 'error' : 'textSecondary'}
                variant='caption'>
                {errorMessage}
              </Typography>
            )}
          </FormControl>
        )
      }}
    />
  )
}

export default MultiSelect
